import { useState, useRef, useEffect } from "react";
import { Phone, X } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { branches } from "../data";

export default function FloatingButtons({ selectedBranch }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const currentBranch = branches[selectedBranch];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleWhatsApp = () => {
    if (!currentBranch?.contact?.whatsapp) {
      alert("WhatsApp number not available for this branch. Please contact us directly.");
      return;
    }

    const message = encodeURIComponent(
      `Hi, I would like to book an appointment at ${currentBranch.shortName} Branch.`
    );

    window.open(
      `https://wa.me/${currentBranch.contact.whatsapp}?text=${message}`,
      "_blank",
      "noopener,noreferrer"
    );
    setIsOpen(false);
  };

  const handleCall = () => {
    if (!currentBranch?.contact?.phone) {
      alert("Phone number not available for this branch. Please contact us directly.");
      return;
    }

    window.location.href = `tel:${currentBranch.contact.phone}`;
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const displayPhone = currentBranch?.contact?.phoneDisplay || "";

  if (!currentBranch) {
    return null;
  }

  return (
    <div className="fixed bottom-5 right-5 z-50" ref={dropdownRef}>
      <button
        type="button"
        onClick={toggleDropdown}
        className="group relative w-14 h-14 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f] rounded-full flex items-center justify-center shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 animate-pulse"
        aria-label="Contact options"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <Phone className="w-6 h-6 text-white" />
        )}
      </button>

      {isOpen && (
        <div className="absolute bottom-16 right-0 mb-2 w-64 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-fade-in-up">
          <div className="px-4 py-3 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f]">
            <p className="text-white text-sm font-semibold">{currentBranch.name}</p>
            <p className="text-white/80 text-xs">{displayPhone}</p>
          </div>

          <button
            type="button"
            onClick={handleCall}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors duration-200 border-b border-gray-100"
          >
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Phone className="w-5 h-5 text-green-600" />
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-800">Call Now</p>
              <p className="text-xs text-gray-500">{displayPhone}</p>
            </div>
          </button>

          <button
            type="button"
            onClick={handleWhatsApp}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors duration-200"
          >
            <div className="w-10 h-10 bg-[#25D366]/10 rounded-full flex items-center justify-center">
              <FaWhatsapp className="w-5 h-5 text-[#25D366]" />
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-800">WhatsApp</p>
              <p className="text-xs text-gray-500">Book via WhatsApp</p>
            </div>
          </button>

          <div className="px-4 py-2 bg-gray-50 rounded-b-2xl">
            <p className="text-xs text-gray-400 text-center">
              {currentBranch.shortName} Branch
            </p>
          </div>
        </div>
      )}
    </div>
  );
}